/* eslint-disable @typescript-eslint/no-empty-function */
import { Lightning } from '@lightningjs/sdk';
import AbstractVideoOverlay from 'components/pages/playback/overlay/AbstractVideoOverlay';
import { constants } from 'aliases';
import VideoDetails from 'components/pages/playback/overlay/VideoDetails';
import CcAudioMenu from 'components/pages/playback/overlay/CcAudioMenu';
import { LiveEventStream, Video } from 'types/api/media';
import { AdCuePoint } from 'components/pages/playback/ad-managers/AbstractAdManager';
import VodControls from 'components/pages/playback/overlay/Controls/VodControls';
import LiveControls from 'components/pages/playback/overlay/Controls/LiveControls';

const PADDING_X = (1920 - constants.ui.progressBarWidth) / 2;
const CONTROL_Y = 1080 - 332;

interface VideoOverlayTemplateSpec extends Lightning.Component.TemplateSpec {
  duration: number;
  mediaContent: Video | LiveEventStream;
  contentType: 'vod' | 'live' | null;
  VideoDetails: typeof VideoDetails;
  VodControls: typeof VodControls;
  LiveControls: typeof LiveControls;
  CcAudioMenu: typeof CcAudioMenu;
}

interface VideoOverlaySignalMap extends Lightning.Component.SignalMap {
  skip(): void;
  seek(): void;
  pause(): void;
  seekPause(): void;
  seekEnd(): void;
  play(): void;
  seeking(): void;
  startOver(): void;
  ccAudio(): void;
  onSubtitlesChange(): void;
  onAudioChange(): void;
  openMenu(): void;
  closeMenu(): void;
  liveButton(): void;
  onControlHover(): void;
}

interface VideoOverlayTypeConfig extends Lightning.Component.TypeConfig {
  SignalMapType: VideoOverlaySignalMap;
}

export default class VideoOverlay extends AbstractVideoOverlay<
  VideoOverlayTemplateSpec,
  VideoOverlayTypeConfig
> {
  private _VideoDetails = this.getByRef('VideoDetails')!;
  private _VodControls = this.getByRef('VodControls')!;
  private _LiveControls = this.getByRef('LiveControls')!;
  private _CcAudioMenu = this.getByRef('CcAudioMenu')!;

  private _isPlayingLive = true;

  _contentType: 'vod' | 'live-dvr' | 'live' | null = null;

  get announce() {
    return this._VideoDetails.announce.join(', ');
  }

  set duration(duration: number) {
    this._VodControls.duration = duration;
  }

  set mediaContent(mediaContent: Video | LiveEventStream) {
    this._VideoDetails.mediaContent = mediaContent;
  }

  set contentType(contentType: 'vod' | 'live-dvr' | 'live' | null) {
    this._contentType = contentType;

    if (this._contentType === 'vod') {
      this._setState('VodControls');
    } else if (this.isLiveContent()) {
      this._LiveControls.hasDVR = this._contentType === 'live-dvr';
      this._setState('LiveControls');
    }
  }

  // Live specific setters
  set isPlayingLive(isPlayingLive: boolean) {
    this._isPlayingLive = isPlayingLive;
    this._LiveControls.isPlayingLive = isPlayingLive;
  }

  set isPlayingStartOfHist(isPlayingStartOfHist: boolean) {
    this._LiveControls.isPlayingStartOfHist = isPlayingStartOfHist;
  }

  set dvrWindowSize(seconds: number) {
    this._LiveControls.dvrWindowSize = seconds;
  }

  set liveSyncPosition(position: number | null) {
    this._LiveControls.liveSyncPosition = position;
  }

  static override _template(): Lightning.Component.Template<VideoOverlayTemplateSpec> {
    return {
      ...super._template(),
      VideoDetails: {
        x: PADDING_X,
        y: 90,
        type: VideoDetails,
      },
      VodControls: {
        x: PADDING_X,
        y: CONTROL_Y,
        type: VodControls,
        passSignals: {
          skip: true,
          seek: true,
          pause: true,
          seekPause: true,
          seekEnd: true,
          play: true,
          seeking: true,
          startOver: true,
          ccAudio: true,
          onControlHover: true,
        },
        visible: false,
      },
      LiveControls: {
        x: PADDING_X,
        y: CONTROL_Y,
        type: LiveControls,
        passSignals: {
          ccAudio: true,
          play: true,
          pause: true,
          skip: true,
          seek: true,
          seekPause: true,
          seekEnd: true,
          seeking: true,
          liveButton: true,
          onControlHover: true,
        },
        visible: false,
      },
      CcAudioMenu: {
        x: constants.ui.ccAudioX,
        y: constants.ui.ccAudioY,
        mountY: 1,
        type: CcAudioMenu,
        passSignals: {
          onSubtitlesChange: true,
          onAudioChange: true,
        },
        visible: false,
      },
    };
  }

  override _setup() {
    super._setup();
  }

  override _active() {
    if (this._contentType === 'vod') {
      this._setState('VodControls');
    } else if (this.isLiveContent()) {
      this._setState('LiveControls');
    } else {
      this._setState('Unset');
    }
  }

  initialize() {
    this._VodControls.duration = 0;
    this.setIsAdPlaying(false);

    if (this._contentType === 'vod') {
      this._VodControls.preloadIcons();
    } else if (this.isLiveContent()) {
      this._LiveControls.preloadIcons();
    }
    this.hide();
  }

  updateCc(ccIndex: number, audioIndex: number, cc: string[], audio: string[]) {
    this._CcAudioMenu.cc = cc;
    this._CcAudioMenu.audio = audio;
    this._CcAudioMenu.ccIndex = ccIndex;
    this._CcAudioMenu.audioIndex = audioIndex;

    this._setState('CcAudioMenu');
  }

  setIsAdPlaying(isAdPlaying: boolean) {
    this._VideoDetails.setSmooth('alpha', isAdPlaying ? 0.5 : 1);
    this._VodControls.isAdPlaying = isAdPlaying;
  }

  adStarted(adDuration: number) {
    this._VodControls.duration = adDuration;
  }

  adBreakEnded(videoDuration: number) {
    this._VodControls.duration = videoDuration;
  }

  updateCuePoints(cuepoints: AdCuePoint[]) {
    this._VodControls.adCuePoints = cuepoints;
  }

  isInMenu() {
    return this._CcAudioMenu.visible;
  }

  handleMediaFFRW(seekType: 'FF' | 'RW') {
    this.currentControls(controls =>
      seekType === 'FF' ? controls.onFastForward() : controls.onRewind(),
    );
  }

  isVideoSeeking() {
    return this._VodControls.isSeeking() || this._LiveControls.isSeeking();
  }

  private currentControls(
    targetFn: (controls: VodControls | LiveControls) => void,
  ) {
    if (this._contentType === 'vod') {
      targetFn(this._VodControls);
    } else if (this.isLiveContent()) {
      targetFn(this._LiveControls);
    }
  }

  private isLiveContent() {
    return this._contentType === 'live' || this._contentType === 'live-dvr';
  }

  // Implemented in state
  updateProgress(relativeTime: number) {}
  updateFromPlay() {}
  updateFromPause() {}

  static override _states() {
    return [
      class Unset extends this {
        override $enter() {
          console.error('Overlay type is unset');
        }
      },

      class LiveControls extends this {
        override _getFocused() {
          return this._LiveControls;
        }

        override $enter() {
          this._VideoDetails.visible = true;
          this._LiveControls.visible = true;
          this._LiveControls.isPlayingLive = this._isPlayingLive;
          this._LiveControls.isLiveStreamActive = true; // TODO: implement this later
        }

        override $exit() {
          this._VideoDetails.visible = false;
          this._LiveControls.visible = false;
        }

        override updateProgress(relativeTime: number) {
          this._LiveControls.liveProgress = relativeTime;
        }

        override updateFromPlay() {
          this._LiveControls.updateFromPlay();
        }

        override updateFromPause() {
          this._LiveControls.updateFromPause();
        }
      },

      class VodControls extends this {
        override _getFocused() {
          return this._VodControls;
        }

        override $enter() {
          this._VideoDetails.visible = true;
          this._VodControls.visible = true;
        }

        override $exit() {
          this._VideoDetails.visible = false;
          this._VodControls.visible = false;
        }

        override updateProgress(relativeTime: number) {
          this._VodControls.progress = relativeTime;
        }

        override updateFromPlay() {
          this._VodControls.updateFromPlay();
        }

        override updateFromPause() {
          this._VodControls.updateFromPause();
        }
      },
      class CcAudioMenu extends this {
        override _getFocused() {
          return this._CcAudioMenu;
        }

        override $enter() {
          this._CcAudioMenu.visible = true;
          this.signal('openMenu');
        }

        override $exit() {
          this._CcAudioMenu.visible = false;
          this.signal('closeMenu');
        }

        override _handleBack() {
          if (this._contentType === 'vod') {
            this._setState('VodControls');
          } else if (this.isLiveContent()) {
            this._setState('LiveControls');
          }
        }
      },
    ];
  }
}
